/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: telephonyDomainsApi.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    API client functions for telephony domains module.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import type { Domain, DomainAlias } from '../types';

export interface DomainListResponse {
  success: boolean;
  data: Domain[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface DomainResponse {
  success: boolean;
  data: Domain;
}

export interface DomainAliasListResponse {
  success: boolean;
  data: DomainAlias[];
}

export interface DomainAliasResponse {
  success: boolean;
  data: DomainAlias;
}

/**
 * Get all domains with pagination and filtering
 */
export async function getDomains(params: {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  search?: string;
  tenantSlug?: string;
  userId?: string;
}): Promise<DomainListResponse> {
  const queryParams = new URLSearchParams({
    page: String(params.page),
    limit: String(params.limit),
    sortBy: params.sortBy,
    sortOrder: params.sortOrder
  });

  if (params.search?.trim()) {
    queryParams.set('search', params.search.trim());
  }

  if (params.tenantSlug) {
    queryParams.set('tenantSlug', params.tenantSlug);
  }

  const response = await fetch(`${buildApiUrl('/api/telephony/domains')}?${queryParams.toString()}`, {
    headers: getApiHeaders(params.tenantSlug, params.userId)
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  return await response.json();
}

/**
 * Get single domain by ID
 */
export async function getDomainById(
  id: string,
  tenantSlug?: string,
  userId?: string
): Promise<DomainResponse> {
  const response = await fetch(buildApiUrl(`/api/telephony/domains/${id}`), {
    headers: getApiHeaders(tenantSlug, userId)
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  return await response.json();
}

/**
 * Create new domain
 */
export async function createDomain(
  data: {
    domainName: string;
    domainDescription?: string;
    tenant_id: number;
    domainEnabled?: boolean;
    language?: string;
    timezone?: string;
    countryCode?: string;
    certificateId?: number | null;
    serviceType?: string;
  },
  tenantSlug?: string,
  userId?: string
): Promise<DomainResponse> {
  const response = await fetch(buildApiUrl('/api/telephony/domains'), {
    method: 'POST',
    headers: {
      ...getApiHeaders(tenantSlug, userId),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `HTTP ${response.status}`);
  }

  return await response.json();
}

/**
 * Update domain
 */
export async function updateDomain(
  id: string,
  data: {
    domainName?: string;
    domainDescription?: string;
    tenant_id?: number;
    domainEnabled?: boolean;
    language?: string;
    timezone?: string;
    countryCode?: string;
    certificateId?: number | null;
    serviceType?: string;
  },
  tenantSlug?: string,
  userId?: string
): Promise<DomainResponse> {
  const response = await fetch(buildApiUrl(`/api/telephony/domains/${id}`), {
    method: 'PUT',
    headers: {
      ...getApiHeaders(tenantSlug, userId),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `HTTP ${response.status}`);
  }

  return await response.json();
}

/**
 * Delete domain
 */
export async function deleteDomain(
  id: string,
  tenantSlug?: string,
  userId?: string
): Promise<void> {
  const response = await fetch(buildApiUrl(`/api/telephony/domains/${id}`), {
    method: 'DELETE',
    headers: getApiHeaders(tenantSlug, userId)
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `HTTP ${response.status}`);
  }
}

// ============================================================================================================
// DOMAIN ALIASES API
// ============================================================================================================

/**
 * Get all aliases for a domain
 */
export async function getDomainAliases(
  domainId: string,
  tenantSlug?: string,
  userId?: string
): Promise<DomainAliasListResponse> {
  const response = await fetch(buildApiUrl(`/api/telephony/domains/${domainId}/aliases`), {
    headers: getApiHeaders(tenantSlug, userId)
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  return await response.json();
}

/**
 * Create a new alias for a domain
 */
export async function createDomainAlias(
  domainId: string,
  data: {
    alias: string;
    description?: string;
    enabled?: boolean;
  },
  tenantSlug?: string,
  userId?: string
): Promise<DomainAliasResponse> {
  const response = await fetch(buildApiUrl(`/api/telephony/domains/${domainId}/aliases`), {
    method: 'POST',
    headers: {
      ...getApiHeaders(tenantSlug, userId),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `HTTP ${response.status}`);
  }

  return await response.json();
}

/**
 * Update an existing domain alias
 */
export async function updateDomainAlias(
  domainId: string,
  aliasId: string,
  data: {
    alias: string;
    description?: string;
    enabled?: boolean;
  },
  tenantSlug?: string,
  userId?: string
): Promise<DomainAliasResponse> {
  const response = await fetch(buildApiUrl(`/api/telephony/domains/${domainId}/aliases/${aliasId}`), {
    method: 'PUT',
    headers: {
      ...getApiHeaders(tenantSlug, userId),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `HTTP ${response.status}`);
  }

  return await response.json();
}

/**
 * Delete a domain alias
 */
export async function deleteDomainAlias(
  domainId: string,
  aliasId: string,
  tenantSlug?: string,
  userId?: string
): Promise<void> {
  const response = await fetch(buildApiUrl(`/api/telephony/domains/${domainId}/aliases/${aliasId}`), {
    method: 'DELETE',
    headers: getApiHeaders(tenantSlug, userId)
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `HTTP ${response.status}`);
  }
}

